import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  SeasonLeaderboardList,
  addAvatarToRanking,
} from '@sorare/core/src/components/rivals/Seasons/SeasonLeaderboardList';

import { NewRecruitHeader } from './NewRecruitHeader';
import { SeasonSquadLeaderboard_footballRivalsSeasonSquad } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;
const FooterWrapper = styled(Vertical)`
  justify-content: flex-end;
  flex: 1;
  padding: 0 var(--unit);
`;

const squadScoresMessage = defineMessage({
  id: 'SeasonSquadLeaderboard.squadScores',
  defaultMessage: 'Squad scores',
});

type MembershipRanking = NonNullable<
  SeasonSquadLeaderboard_footballRivalsSeasonSquad['membershipsLeaderboard']
>['rankings'][number];

type Props = {
  seasonSquad: SeasonSquadLeaderboard_footballRivalsSeasonSquad;
  onRowClick?: (selectedUserSlug: string) => void;
  footer?: ReactNode;
};
export const SeasonSquadLeaderboard = ({
  seasonSquad,
  onRowClick,
  footer,
}: Props) => {
  const { formatMessage } = useIntl();
  const { membershipsLeaderboard, topManagerScoresCount } = seasonSquad;

  if (!membershipsLeaderboard) {
    return null;
  }

  const { rankings, endDate } = membershipsLeaderboard;

  const { newRecruitRankings, scoresCountRankings, rest } = [
    ...rankings,
  ].reduce<{
    newRecruitRankings: MembershipRanking[];
    scoresCountRankings: MembershipRanking[];
    rest: MembershipRanking[];
  }>(
    (obj, current, i) => {
      if (current.newRecruit) {
        obj.newRecruitRankings.push(current);
      } else if (i < topManagerScoresCount) {
        obj.scoresCountRankings.push(current);
      } else {
        obj.rest.push(current);
      }
      return obj;
    },
    { newRecruitRankings: [], scoresCountRankings: [], rest: [] }
  );

  return (
    <Root gap={0}>
      <SeasonLeaderboardList
        promotedSectionTitle={formatMessage(squadScoresMessage)}
        promotedRankings={scoresCountRankings.map(addAvatarToRanking)}
        unrelegatedRankings={rest.map(addAvatarToRanking)}
        relegatedRankings={[]}
        onRowClick={onRowClick}
        additionalStyles={
          {
            '--promoted-color': 'var(--c-rivals-purple-600)',
            '--gradient-promoted-color': 'var(--c-neutral-100)',
            '--promoted-title-color': 'var(--c-rivals-purple-600)',
          } as CSSProperties
        }
      />
      {newRecruitRankings.length > 0 && (
        <>
          <NewRecruitHeader
            newRecruitsCount={newRecruitRankings.length}
            endDate={endDate}
          />
          <SeasonLeaderboardList
            hideHeader
            promotedRankings={[]}
            unrelegatedRankings={newRecruitRankings.map(addAvatarToRanking)}
            relegatedRankings={[]}
            onRowClick={onRowClick}
          />
        </>
      )}
      {FooterWrapper && <FooterWrapper>{footer}</FooterWrapper>}
    </Root>
  );
};

SeasonSquadLeaderboard.fragments = {
  footballRivalsSeasonSquad: gql`
    fragment SeasonSquadLeaderboard_footballRivalsSeasonSquad on FootballRivalsSeasonSquad {
      id
      topManagerScoresCount
      membershipsLeaderboard {
        id
        endDate
        rankings {
          id
          newRecruit
          captain
          points
          ranking
          cumulatedScoreDifference
          liveGamesCount
          upcomingGamesCount
          eligibleReward {
            probabilisticBundleConfiguration {
              id
              pictureUrl
            }
          }
          manager {
            id
            user {
              slug
              nickname
              ...addAvatarToRanking_user
            }
          }
        }
      }
    }
    ${addAvatarToRanking.fragments.user}
  ` as TypedDocumentNode<SeasonSquadLeaderboard_footballRivalsSeasonSquad>,
};
